/**
 * DEMO DATA
 * ---------------------------------------------------------------------------
 * Fixture content for the screens that do not have a backend yet: Morning
 * brief, Decision Room, Semantic layer, Agents, Trust and the Decision log.
 *
 * Everything the demo invents lives in this file so it is obvious what is real
 * and what is not. When an endpoint exists, swap the import for a hook and
 * delete the matching block here - the components take the same shapes.
 */

export const DEMO = true;

/* ---------------------------------------------------------------- sources */

export interface DemoSource {
  id: string;
  name: string;
  kind: string;
  abbr: string;
  color: string;
  how: string;
  fresh: string;
  records: string;
  mapped: number;
  objects: string;
}

export const SOURCES: DemoSource[] = [
  { id: 'sf', name: 'Salesforce', kind: 'CRM', abbr: 'SF', color: '#00A1E0', how: 'Vendor MCP server', fresh: '2 min', records: '412K', mapped: 94, objects: 'Opportunities, Accounts, Cases, Forecast' },
  { id: 'sap', name: 'SAP S/4HANA', kind: 'ERP', abbr: 'SAP', color: '#0B66C3', how: 'OData via MCP', fresh: '11 min', records: '8.4M', mapped: 88, objects: 'GL, AR/AP, Sales orders, Inventory' },
  { id: 'odoo', name: 'Odoo 19', kind: 'ERP', abbr: 'OD', color: '#714B67', how: 'Odoo MCP server', fresh: '6 min', records: '1.2M', mapped: 91, objects: 'Accounting, Inventory, Manufacturing' },
  { id: 'zoho', name: 'Zoho CRM + Books', kind: 'CRM / Finance', abbr: 'ZO', color: '#E42527', how: 'REST API connector', fresh: '14 min', records: '640K', mapped: 86, objects: 'Leads, Deals, Invoices' },
  { id: 'd365', name: 'Dynamics 365', kind: 'ERP', abbr: 'D3', color: '#0078D4', how: 'Dataverse connector', fresh: '9 min', records: '2.1M', mapped: 82, objects: 'Finance, Supply chain' },
  { id: 'ns', name: 'Oracle NetSuite', kind: 'ERP', abbr: 'NS', color: '#1B3A5C', how: 'NetSuite AI Connector', fresh: '18 min', records: '920K', mapped: 79, objects: 'Subsidiaries, Consolidation' },
  { id: 'wd', name: 'Workday', kind: 'HR', abbr: 'WD', color: '#F5A623', how: 'REST API connector', fresh: '1 h', records: '48K', mapped: 90, objects: 'Headcount, Comp, Attrition' },
  { id: 'hs', name: 'HubSpot', kind: 'Marketing', abbr: 'HS', color: '#FF7A59', how: 'Vendor MCP server', fresh: '4 min', records: '2.8M', mapped: 88, objects: 'Contacts, Campaigns, Attribution' },
  { id: 'snow', name: 'Snowflake', kind: 'Warehouse', abbr: 'SN', color: '#29B5E8', how: 'SQL via semantic layer', fresh: 'Live', records: '184M', mapped: 97, objects: 'Product telemetry, history' },
  { id: 'sp', name: 'SharePoint', kind: 'Documents', abbr: 'SP', color: '#038387', how: 'Document connector', fresh: '22 min', records: '38K docs', mapped: 71, objects: 'Board packs, contracts, policies' },
  { id: 'gd', name: 'Google Drive', kind: 'Documents', abbr: 'GD', color: '#1FA463', how: 'Document connector', fresh: '30 min', records: '12K docs', mapped: 68, objects: 'Plans, decks, models' },
  { id: 'xl', name: 'Excel models', kind: 'Spreadsheets', abbr: 'XL', color: '#107C41', how: 'File ingestion + parser', fresh: 'Yesterday', records: '412 files', mapped: 54, objects: 'Budget FY26, regional forecasts' },
  { id: 'mail', name: 'Email & meetings', kind: 'Unstructured', abbr: 'EM', color: '#6D5AE6', how: 'Graph / Gmail API', fresh: '5 min', records: 'Indexed', mapped: 62, objects: 'Threads, meeting notes' },
];

/* --------------------------------------------------------- morning brief */

export const BRIEF_KPIS = [
  { label: 'Revenue QTD', value: '$14.8M', color: 'var(--blue)', icon: 'money', pip: '92% to plan', dir: '' },
  { label: 'Gross margin', value: '37.6', unit: '%', color: 'var(--red)', icon: 'chart', pip: '▼ 3.6 pts', dir: 'dn' },
  { label: 'Cash', value: '$8.1M', color: 'var(--green)', icon: 'money', sub: '14 months runway' },
  { label: 'Pipeline Q4', value: '$42M', color: 'var(--purple)', icon: 'target', pip: '▲ 18%', dir: 'up' },
  { label: 'Headcount', value: '612', color: 'var(--teal)', icon: 'people', pip: '▲ 4', dir: 'up' },
] as const;

export const BRIEF_ITEMS = [
  {
    kind: 'Decide',
    tone: 'r',
    title: 'Q3 discounting is running 6 points above plan',
    body: 'Enterprise average discount is 19% against a 13% plan. At this rate Q3 margin lands near 36%. The deal-desk threshold is being overridden, not enforced.',
    sources: 'Salesforce · SAP',
    ask: 'Why did our gross margin drop last quarter?',
  },
  {
    kind: 'Watch',
    tone: 'a',
    title: 'Cairo Fintech Group is 96 days overdue',
    body: '$1.2M outstanding. Your cash model assumes it lands in November; nothing in SAP or email supports that.',
    sources: 'SAP · Email',
    ask: 'How much cash will we have at the end of Q4?',
  },
  {
    kind: 'Opportunity',
    tone: 'g',
    title: 'KSA pipeline crossed your 2027 threshold',
    body: '$14.2M, up 62%. Your strategy document set $12M as the trigger for a local presence.',
    sources: 'Salesforce · SharePoint',
    ask: 'Should we open a regional office in Riyadh next year?',
  },
  {
    kind: 'FYI',
    tone: 'b',
    title: 'Two systems disagree on Q2 revenue',
    body: 'Salesforce is $180K above SAP — two deals won but not yet invoiced. No action, but the board pack should use SAP.',
    sources: 'Salesforce · SAP',
    ask: 'Which deals are counted in Salesforce but not invoiced in SAP?',
  },
] as const;

export const BRIEF_CHANGES = [
  { label: 'Salesforce', value: '38 deals updated · 2 closed won' },
  { label: 'SAP S/4HANA', value: '214 invoices posted' },
  { label: 'Odoo (Egypt)', value: 'Month-end journals posted' },
  { label: 'Workday', value: '3 joiners, 1 leaver' },
  { label: 'Excel models', value: 'Budget_FY26_v8 uploaded', color: 'var(--amber)' },
  { label: 'Board pack', value: 'Draft v2 in SharePoint' },
] as const;

export const WEEK_DECISIONS = [
  { name: 'Riyadh regional office', owner: 'Ahmed Hassan', due: 'Board · 24 Sep', ready: 55, of: '6/11', status: 'Inputs missing', tone: 'a' },
  { name: 'Q3 discount cap', owner: 'Yara Rostom', due: 'Thu 17 Sep', ready: 90, of: '9/10', status: 'Ready', tone: 'g' },
  { name: '2027 price increase', owner: 'Salma Adel', due: 'Oct', ready: 75, of: '9/12', status: 'Scoping', tone: 'b' },
  { name: 'Cairo Fintech escalation', owner: 'Omar Shafik', due: 'Tue 15 Sep', ready: 100, of: '4/4', status: 'Overdue', tone: 'r' },
] as const;

/* ---------------------------------------------------------- decision room */

export const DECISION = {
  eyebrow: 'Decision · owner Ahmed Hassan · for the board on Thursday 24 September',
  question: 'Should we open a regional office in Riyadh in 2027, and if so, at what size?',
  readiness: 55,
  readinessNote: 'directionally positive, not yet costed',
  facts: [
    { label: 'Readiness', value: '55% · 6 of 11 inputs' },
    { label: 'Value at stake', value: '$14.2M pipeline' },
    { label: 'Reversibility', value: 'Low · 3-year lease' },
    { label: 'Deadline', value: '9 working days' },
  ],
  inputs: [
    { state: 'done', name: 'KSA pipeline, win rate and deal size', source: 'Salesforce', owner: 'Automatic', detail: '$14.2M · 31% win rate · $460K avg' },
    { state: 'done', name: 'GCC cost-to-serve and margin', source: 'SAP S/4HANA', owner: 'Automatic', detail: '18.4% vs 11.2% Egypt' },
    { state: 'done', name: 'Strategic threshold for local presence', source: 'SharePoint', owner: 'Automatic', detail: '$12M pipeline, set in Strategy_2027' },
    { state: 'done', name: 'GCC headcount and travel spend', source: 'Workday + SAP', owner: 'Automatic', detail: '9 FTE · $620K travel TTM' },
    { state: 'done', name: 'Customer concentration in KSA', source: 'Salesforce', owner: 'Automatic', detail: 'Top 3 = 58% of pipeline' },
    { state: 'done', name: 'Competitor presence', source: 'Win/loss notes', owner: 'Automatic', detail: '2 competitors local, 1 remote' },
    { state: 'part', name: 'Conversion rate with local presence', source: 'Modelled from UAE', owner: 'OmniMind', detail: 'UAE win rate rose 31% → 42% in year one' },
    { state: 'miss', name: 'Local salary benchmarks and hiring cost', source: 'Hisham Fouad · HR', owner: 'Due Tue', detail: 'Requested 2 days ago' },
    { state: 'miss', name: 'Saudization and entity requirements', source: 'Legal counsel', owner: 'Due Wed', detail: 'Requested 2 days ago' },
    { state: 'miss', name: 'Office and lease cost', source: 'Karim Nabil · Ops', owner: 'Due Wed', detail: 'Not started' },
    { state: 'part', name: 'Board appetite and capital allocation', source: 'Last board pack', owner: 'Partial', detail: 'Q2 minutes mention GCC' },
  ],
  options: [
    { key: 'A', title: 'Full office, 12 FTE', blurb: 'Local sales, pre-sales and support from Q1 2027.', cost: '$2.4M', payback: 'Year 3', risk: 'High', note: 'Best if the top 3 accounts close' },
    { key: 'B', title: 'Lean hub, 4 FTE + partner', blurb: 'Two sellers and two pre-sales, delivery through a certified partner.', cost: '$0.8M', payback: 'Year 2', risk: 'Medium', note: 'Recommended — protects against concentration', recommended: true },
    { key: 'C', title: 'Stay remote, add travel', blurb: 'Keep serving from Cairo with a dedicated GCC team.', cost: '$0.3M', payback: 'Year 1', risk: 'Low', note: 'Cost-to-serve stays at 18%' },
  ],
  scenario: [
    { assumption: 'KSA win rate', base: '31%', needed: '≥ 36%', source: 'Salesforce, UAE analogue', confidence: 'medium', tone: 'a' },
    { assumption: 'Average deal size', base: '$460K', needed: '≥ $420K', source: 'Salesforce', confidence: 'high', tone: 'g' },
    { assumption: 'Deals closed per year', base: '11', needed: '≥ 9', source: 'Salesforce pipeline', confidence: 'high', tone: 'g' },
    { assumption: 'Cost-to-serve after local hub', base: '18.4%', needed: '≤ 13%', source: 'SAP, UAE analogue', confidence: 'medium', tone: 'a' },
    { assumption: 'Partner margin share', base: '—', needed: '≤ 22%', source: 'Not in any system', confidence: 'unknown', tone: 'r' },
  ],
  watch:
    '58% of the KSA pipeline sits in three accounts. If one of them slips, the case weakens sharply. A partner-led model carries that risk at a fraction of the fixed cost.',
} as const;

/* -------------------------------------------------------- semantic layer */

export const METRIC_CONFLICTS = [
  { term: 'Revenue', sf: 'Closed Won amount', sap: 'Invoiced, net of credits', odoo: 'Invoiced, gross', excel: 'Budget plan', certified: 'SAP logic', tone: 'g', owner: 'CFO' },
  { term: 'Gross margin', sf: '—', sap: '(Rev − COGS) / Rev', odoo: 'Excl. freight', excel: 'Incl. freight', certified: 'SAP, incl. freight', tone: 'g', owner: 'CFO' },
  { term: 'Customer', sf: 'Account', sap: 'Sold-to party', odoo: 'Partner', excel: 'Mixed', certified: 'Sold-to party', tone: 'g', owner: 'COO' },
  { term: 'Active customer', sf: 'Open opp in 12m', sap: 'Invoiced in 12m', odoo: '—', excel: '—', certified: 'pending', tone: 'a', owner: 'CRO' },
  { term: 'Headcount', sf: '—', sap: '—', odoo: '—', excel: 'FTE incl. contractors', certified: 'Workday, FTE only', tone: 'g', owner: 'CHRO' },
  { term: 'Backlog', sf: 'Unbilled Closed Won', sap: 'Open sales orders', odoo: 'Open orders', excel: '—', certified: 'pending', tone: 'a', owner: 'CFO' },
] as const;

export const CERTIFIED_METRICS = [
  { id: 'REV-01', name: 'Revenue', definition: 'Invoiced amount net of credit notes, all entities, in reporting currency at month-end rate', source: 'SAP S/4HANA + Odoo', grain: 'Invoice line', by: 'Salma Adel, CFO', version: 'v3' },
  { id: 'GM-01', name: 'Gross margin', definition: '(REV-01 − COGS incl. inbound freight) ÷ REV-01', source: 'SAP S/4HANA', grain: 'Product × month', by: 'Salma Adel, CFO', version: 'v2' },
  { id: 'CASH-02', name: 'Cash position', definition: 'Bank balances across all entities, EGP and USD, at spot', source: 'SAP bank feed', grain: 'Account × day', by: 'Salma Adel, CFO', version: 'v1' },
  { id: 'PIPE-01', name: 'Qualified pipeline', definition: 'Open opportunities at Qualification or later, weighted by stage', source: 'Salesforce + Zoho', grain: 'Opportunity', by: 'Yara Rostom, CRO', version: 'v4' },
  { id: 'NRR-01', name: 'Net revenue retention', definition: 'Revenue from a cohort now ÷ same cohort 12 months ago', source: 'SAP', grain: 'Customer × month', by: 'Salma Adel, CFO', version: 'v2' },
  { id: 'HC-01', name: 'Headcount', definition: 'Active FTE employees; contractors excluded', source: 'Workday', grain: 'Person', by: 'Hisham Fouad, CHRO', version: 'v1' },
] as const;

/* ----------------------------------------------------------------- agents */

export const PIPELINE_STEPS = [
  { n: '1', title: 'Understand', body: 'Classify the question: lookup, diagnosis, forecast, or decision. A decision triggers decomposition into required inputs.', color: '#D97757' },
  { n: '2', title: 'Resolve terms', body: 'Map every business word to a certified metric. Uncertified terms are flagged, not guessed.', color: '#6D5AE6' },
  { n: '3', title: 'Plan', body: "Choose the agents and queries. Check the user's permissions before a single call is made.", color: '#2354F0' },
  { n: '4', title: 'Retrieve', body: 'Agents query their systems in parallel through governed connectors. Every row carries its source ID.', color: '#0E9B8E' },
  { n: '5', title: 'Reconcile', body: 'Compare overlapping figures across systems. Disagreements are reported with both values.', color: '#D9910E' },
  { n: '6', title: 'Reason', body: 'The model reasons only over retrieved facts. A number that is not in the evidence cannot appear in the answer.', color: '#12B981' },
  { n: '7', title: 'Score', body: 'Confidence from coverage, freshness and conflicts. Gaps become "data required" items.', color: '#CE4630' },
] as const;

export const AGENT_ROSTER = [
  { name: 'Planner', role: 'Decides which agents to call', color: '#D97757' },
  { name: 'Reconciler', role: 'Cross-checks overlapping figures', color: '#D9910E' },
  { name: 'Salesforce agent', role: 'SOQL via vendor MCP', color: '#00A1E0' },
  { name: 'SAP agent', role: 'OData, knows your company codes', color: '#0B66C3' },
  { name: 'Odoo agent', role: 'JSON-RPC via Odoo MCP', color: '#714B67' },
  { name: 'Zoho agent', role: 'CRM + Books REST', color: '#E42527' },
  { name: 'Workday agent', role: 'Headcount and comp', color: '#F5A623' },
  { name: 'Document agent', role: 'Reads contracts, board packs', color: '#038387' },
  { name: 'Spreadsheet agent', role: 'Parses models, detects versions', color: '#107C41' },
  { name: 'Forecast agent', role: 'Runs scenarios on retrieved data', color: '#6D5AE6' },
] as const;

export const AGENT_ACTIVITY = [
  { time: '06:51', question: 'Why did gross margin drop last quarter?', agents: 'SAP, Odoo, SF, Excel', rows: '48,210', conflicts: '1', confidence: '84%', user: 'Ahmed H.' },
  { time: '06:48', question: 'Cash at end of Q4', agents: 'SAP, Workday, SF, Excel', rows: '12,904', conflicts: '1', confidence: '72%', user: 'Ahmed H.' },
  { time: '06:30', question: 'Morning brief assembly', agents: 'All 13', rows: '214,880', conflicts: '3', confidence: '—', user: 'scheduled' },
  { time: 'Yesterday', question: 'Top 10 overdue customers', agents: 'SAP, Odoo', rows: '1,840', conflicts: '0', confidence: '96%', user: 'Salma A.' },
  { time: 'Yesterday', question: 'Q3 pipeline by region', agents: 'SF, Zoho', rows: '3,212', conflicts: '2', confidence: '91%', user: 'Yara R.' },
] as const;

/* ------------------------------------------------------------------ trust */

export const TRUST_RULES = [
  ['Every number is cited', 'Each figure links to the record, query and system it came from.'],
  ['Permissions are inherited', 'A VP sees what their ERP role allows — never more because they asked a chatbot.'],
  ['No evidence, no answer', 'If the data is not there, OmniMind says what is missing instead of estimating silently.'],
  ['Conflicts are shown, not resolved quietly', 'When systems disagree you see both values and which definition won.'],
  ['Read-only by default', 'OmniMind cannot post, approve or send anything. Write actions need an explicit grant and a human confirmation.'],
  ['Files never outrank systems', 'A spreadsheet that disagrees with the ERP is flagged, not trusted.'],
  ['Every question is logged', 'Who asked, what was read, what was answered — replayable for audit.'],
] as const;

export const ROLE_SCOPES = [
  { role: 'CEO', scope: 'All entities', finance: ['full', 'g'], people: ['aggregated', 'a'], board: ['yes', 'g'] },
  { role: 'CFO', scope: 'All entities', finance: ['full', 'g'], people: ['aggregated', 'a'], board: ['yes', 'g'] },
  { role: 'CRO', scope: 'Commercial', finance: ['revenue only', 'a'], people: ['none', 'n'], board: ['no', 'n'] },
  { role: 'CHRO', scope: 'People', finance: ['payroll only', 'a'], people: ['full', 'g'], board: ['no', 'n'] },
  { role: 'Country MD', scope: 'Own entity', finance: ['own entity', 'a'], people: ['own entity', 'a'], board: ['no', 'n'] },
  { role: 'Board member', scope: 'Board pack scope', finance: ['summary', 'a'], people: ['none', 'n'], board: ['yes', 'g'] },
] as const;

export const AUDIT_LOG = [
  { time: '06:51', user: 'Ahmed Hassan', question: 'Why did gross margin drop…', sources: 'SAP, Odoo, SF, Excel', outcome: 'answered · 84%', tone: 'g' },
  { time: '06:12', user: 'Yara Rostom', question: 'Show me CFO compensation', sources: '—', outcome: 'declined · no access', tone: 'r' },
  { time: 'Yesterday', user: 'Salma Adel', question: 'Forecast Q4 with Budget v8', sources: 'SAP, Excel', outcome: 'answered · uncertified input flagged', tone: 'a' },
  { time: 'Yesterday', user: 'Board · M. Fathy', question: 'Q2 margin vs plan', sources: 'Board pack scope', outcome: 'answered · 96%', tone: 'g' },
  { time: 'Yesterday', user: 'Tarek Ghanem', question: 'Competitor pricing in KSA', sources: '—', outcome: 'declined · no evidence held', tone: 'n' },
] as const;

/* ----------------------------------------------------------- decision log */

export const DECISION_LOG = [
  { name: 'UAE market entry', date: 'Mar 2025', readiness: '84%', choice: 'Lean hub + partner', outcome: 'Win rate 31% → 42%', verdict: 'evidence held', tone: 'g' },
  { name: 'Discount cap at 15%', date: 'Jan 2026', readiness: '91%', choice: 'Enforced via deal desk', outcome: 'Margin +1.8 pts in Q1', verdict: 'evidence held', tone: 'g' },
  { name: 'Warehouse consolidation', date: 'Nov 2025', readiness: '52%', choice: 'Consolidated to 2 sites', outcome: 'Freight up 23%', verdict: 'decided early', tone: 'r' },
  { name: 'Hire 8 SDRs', date: 'Feb 2026', readiness: '71%', choice: 'Hired 6', outcome: 'Pipeline +38%', verdict: 'evidence held', tone: 'g' },
  { name: 'Sunset SMB plan', date: 'Apr 2026', readiness: '58%', choice: 'Sunset', outcome: 'Churn above forecast', verdict: 'partly', tone: 'a' },
] as const;
